import React, { useState } from 'react';
import { proFetch } from '../../utils/proApi';
import Spotlight from './Spotlight';

// Studio "Avant / Après" : photo "avant" (S3) + travaux de la pièce + palette de
// style + CONSIGNES libres (style décrit par le client en audio + notes du métreur)
// → rendu "après" édité en place par le modèle d'image (voir pro/pro.afters.js). Régénérable autant que voulu.
// Le résultat est stocké dans room.afterPhoto ; les consignes dans room.aiBrief.
export default function AfterStudio({ leadRef, room, style, clientBrief = '', onChange, onBriefChange }) {
  const photos = (room.photos || []).filter((p) => p && p.id); // uniquement les photos uploadées (S3)
  const after = room.afterPhoto || null;
  const [beforeId, setBeforeId] = useState(after?.fromPhotoId || photos[0]?.id || '');
  // Consignes par défaut : style client (audio) + notes terrain de la pièce.
  const defaultBrief = [clientBrief, room.notes].map((s) => (s || '').trim()).filter(Boolean).join('\n');
  const [brief, setBrief] = useState(room.aiBrief != null ? room.aiBrief : defaultBrief);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [spot, setSpot] = useState(null);   // { photos, i }

  const beforePhoto = photos.find((p) => p.id === beforeId) || photos[0] || null;

  const editBrief = (v) => { setBrief(v); if (onBriefChange) onBriefChange(v); };

  // Tâche de fond côté serveur, suivie toutes les 3 s : l'appel direct durait
  // plus que les 26 s du proxy Netlify et finissait en « Unexpected end of
  // JSON input » — la réponse coupée avant d'exister.
  const generate = async () => {
    if (!beforePhoto?.id) { setErr('Choisis une photo « Avant » (uploadée).'); return; }
    setBusy(true); setErr('');
    try {
      const res = await proFetch(`/leads/${leadRef}/afters`, {
        method: 'POST',
        body: JSON.stringify({
          style: style || undefined,
          pieces: [{ roomId: room.id, nom: room.customLabel || room.label, beforePhotoId: beforePhoto.id, lines: room.lines || [], brief: (brief || '').trim() || undefined }],
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Génération échouée');
      for (;;) {
        await new Promise(ok => setTimeout(ok, 3000));
        const s = await proFetch(`/leads/${leadRef}/afters/${data.tache}`);
        const suivi = await s.json().catch(() => ({}));
        if (!s.ok) throw new Error(suivi.error || 'Suivi impossible');
        if (suivi.etat === 'erreur') throw new Error(suivi.erreur || 'Génération échouée');
        if (suivi.etat === 'termine') {
          const r = (suivi.resultats || [])[0];
          if (!r || r.erreur) throw new Error(r?.erreur || 'Aucun rendu produit');
          // `avant` : la photo découpée dans un document scanné, qui devient l'« avant ».
          onChange({ id: r.id, url: r.url, fromPhotoId: r.fromPhotoId || beforePhoto.id, avant: r.avant || undefined });
          break;
        }
      }
    } catch (e) {
      setErr(e.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (!photos.length) {
    return <p className="text-xs text-slate-400">Ajoute une photo de la pièce (uploadée) pour générer un rendu « après ».</p>;
  }

  return (
    <div>
      {/* 1. Photo "Avant" */}
      <p className="text-xs font-semibold text-slate-500 mb-2">1. Photo « Avant »</p>
      <div className="flex gap-2 flex-wrap mb-3">
        {photos.map((p) => (
          <span key={p.id} className="relative w-16 h-16">
            <button
              onClick={() => setBeforeId(p.id)}
              className={`block w-16 h-16 rounded-lg overflow-hidden border-2 ${p.id === beforeId ? 'border-orange-500' : 'border-slate-200'}`}
              title={p.caption || ''}
            >
              <img src={p.url} alt="" className="w-full h-full object-cover" />
              {p.id === beforeId && <span className="absolute inset-x-0 bottom-0 bg-orange-500 text-white text-[9px] font-bold text-center">Avant</span>}
            </button>
            <button type="button" title="Voir en grand" aria-label="Voir en grand"
                    onClick={() => setSpot({ photos: photos.map(x => ({ url: x.url, legende: x.caption || '' })), i: photos.indexOf(p) })}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white/90 shadow text-[11px] leading-5 text-center text-slate-700 hover:bg-white">🔍</button>
          </span>
        ))}
      </div>

      {/* 2. Consignes IA — style client (audio) + notes métreur, éditables */}
      <p className="text-xs font-semibold text-slate-500 mb-1">
        2. Consignes pour l'IA <span className="font-normal text-slate-400">— style décrit par le client + notes terrain</span>
      </p>
      <textarea
        value={brief}
        onChange={(e) => editBrief(e.target.value)}
        rows={3}
        placeholder="Ex : ambiance chaleureuse, parquet chêne clair, murs blanc cassé, garder la cheminée, esprit scandinave…"
        className="w-full text-sm border border-slate-200 rounded-lg p-2 mb-1 resize-y focus:border-orange-400 focus:ring-1 focus:ring-orange-200 outline-none"
      />
      <p className="text-[11px] text-slate-400 mb-3">
        L'IA suit ces consignes pour les couleurs / matériaux / ambiance. Modifie-les et <strong>régénère autant que nécessaire</strong> avant de finaliser le dossier.
      </p>

      {/* 3. Générer / régénérer */}
      <button
        onClick={generate}
        disabled={busy || !beforePhoto?.id}
        className="px-4 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm"
      >
        {busy ? 'Génération… (~30 s)' : after ? '↻ Régénérer l\'après' : '✨ Générer l\'après'}
      </button>
      {err && <p className="text-xs text-red-600 mt-2">{err}</p>}

      {/* Aperçu Avant | Après */}
      {(beforePhoto || after) && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          <figure className="rounded-lg overflow-hidden border border-slate-200 cursor-zoom-in" title="Voir en grand"
                  onClick={() => beforePhoto && setSpot({ photos: [{ url: beforePhoto.url, legende: 'Aujourd’hui' }, ...(after ? [{ url: after.url, legende: 'Après rénovation' }] : [])], i: 0 })}>
            {beforePhoto && <img src={beforePhoto.url} alt="Avant" className="w-full h-40 object-cover" />}
            <figcaption className="text-[10px] font-bold text-slate-500 px-2 py-1 bg-slate-50">AUJOURD'HUI</figcaption>
          </figure>
          <figure className={`rounded-lg overflow-hidden border border-orange-200 ${after ? 'cursor-zoom-in' : ''}`} title={after ? 'Voir en grand' : ''}
                  onClick={() => after && setSpot({ photos: [...(beforePhoto ? [{ url: beforePhoto.url, legende: 'Aujourd’hui' }] : []), { url: after.url, legende: 'Après rénovation' }], i: beforePhoto ? 1 : 0 })}>
            {after
              ? <img src={after.url} alt="Après" className="w-full h-40 object-cover" />
              : <div className="w-full h-40 bg-slate-50 flex items-center justify-center text-xs text-slate-400 text-center px-3">Le rendu « après » apparaîtra ici</div>}
            <figcaption className="text-[10px] font-bold text-orange-600 px-2 py-1 bg-orange-50">APRÈS RÉNOVATION</figcaption>
          </figure>
        </div>
      )}
      {spot && <Spotlight photos={spot.photos} index={spot.i} onIndex={i => setSpot(s => ({ ...s, i }))} onClose={() => setSpot(null)} />}
    </div>
  );
}
